const DEFAULT_HANDLE_CLASS = 'drawer-edge-resize-handle';

/**
 * Drawer·사이드바 가장자리 드래그로 width 조절 (DOM 플러그인)
 *
 * ctx:
 *   edge?: 'left' | 'right'
 *   minWidth?, maxWidth?
 *   storageKey?: string — storage adapter 키
 *   storage?: { get(key): Promise<number|null>, set(key, width): Promise<void> }
 *   getWidth?, setWidth?, onResize?, handleClass?
 */
export const drawerEdgeResizePlugin = {
  id: 'drawer-edge-resize',

  attach(containerEl, ctx = {}) {
    if (!containerEl) {
      return undefined;
    }

    const edge = ctx.edge === 'left' ? 'left' : 'right';
    const minWidth = ctx.minWidth ?? 160;
    const maxWidth = ctx.maxWidth ?? 600;
    const storageKey = ctx.storageKey;
    const storage = ctx.storage;

    const readWidth = () => {
      if (typeof ctx.getWidth === 'function') {
        return ctx.getWidth();
      }
      return containerEl.getBoundingClientRect().width;
    };

    const clampWidth = width => Math.min(maxWidth, Math.max(minWidth, width));

    const persistWidth = width => {
      if (!storage || !storageKey) {
        return;
      }
      storage.set(storageKey, width).catch(error => {
        console.warn('[drawerEdgeResize] width 저장 실패:', error);
      });
    };

    const applyWidth = (width, { persist = false } = {}) => {
      const clamped = clampWidth(width);
      containerEl.style.width = `${clamped}px`;
      containerEl.style.flexShrink = '0';
      containerEl.style.maxWidth = 'none';
      ctx.setWidth?.(clamped);
      if (persist) {
        persistWidth(clamped);
      }
      ctx.onResize?.(clamped);
      return clamped;
    };

    if (storage && storageKey) {
      storage
        .get(storageKey)
        .then(saved => {
          if (saved != null && !Number.isNaN(saved)) {
            applyWidth(saved);
          }
        })
        .catch(error => {
          console.warn('[drawerEdgeResize] width 불러오기 실패:', error);
        });
    }

    const handle = document.createElement('div');
    handle.setAttribute('role', 'separator');
    handle.setAttribute('aria-orientation', 'vertical');
    handle.setAttribute('aria-label', 'Resize panel');
    handle.className = ctx.handleClass ?? DEFAULT_HANDLE_CLASS;
    handle.style.position = 'absolute';
    handle.style.top = '0';
    handle.style.width = '6px';
    handle.style.height = '100%';
    handle.style.cursor = 'col-resize';
    handle.style.zIndex = '4';
    handle.style.touchAction = 'none';
    if (edge === 'left') {
      handle.style.left = '-3px';
      handle.style.right = 'auto';
    } else {
      handle.style.right = '-3px';
      handle.style.left = 'auto';
    }

    if (getComputedStyle(containerEl).position === 'static') {
      containerEl.style.position = 'relative';
    }
    containerEl.appendChild(handle);

    let dragging = false;
    let startX = 0;
    let startWidth = 0;

    const onPointerDown = event => {
      if (event.button !== 0) {
        return;
      }
      dragging = true;
      startX = event.clientX;
      startWidth = readWidth();
      handle.setPointerCapture(event.pointerId);
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'col-resize';
      event.preventDefault();
    };

    const onPointerMove = event => {
      if (!dragging) {
        return;
      }
      const delta = edge === 'right' ? event.clientX - startX : startX - event.clientX;
      applyWidth(startWidth + delta);
    };

    const endDrag = event => {
      if (!dragging) {
        return;
      }
      dragging = false;
      try {
        handle.releasePointerCapture(event.pointerId);
      } catch {
        /* pointer already released */
      }
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      applyWidth(readWidth(), { persist: true });
    };

    handle.addEventListener('pointerdown', onPointerDown);
    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', endDrag);
    handle.addEventListener('pointercancel', endDrag);

    const current = readWidth();
    if (current > 0) {
      applyWidth(current);
    }

    return () => {
      handle.removeEventListener('pointerdown', onPointerDown);
      handle.removeEventListener('pointermove', onPointerMove);
      handle.removeEventListener('pointerup', endDrag);
      handle.removeEventListener('pointercancel', endDrag);
      handle.remove();
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
  }
};
